import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Avatar, Chip, Grid, Skeleton, IconButton, TextField, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import styled from '@emotion/styled';
import { getTimeProfile } from '../redux/ducks/time';
import { updateUser, uploadUserAvatar, removeUserAvatar, deactivateUser } from '../redux/slices/users';
import DeactivateUserDialog from './DeactivateUserDialog';
import SetUserPasswordDialog from './SetUserPasswordDialog';
import { showAlert } from '../redux/slices/alerts';
import { useDispatch, useSelector } from 'react-redux';
import { convertMinutesToHours } from '../utils/timeUtils';
import { Check, Close, CloudUpload, Edit, PersonRemove, LockReset } from '@mui/icons-material';
import { useHasPermission } from '../utils/permissionUtils';
import { getAvatarUrl } from '../utils/avatarUtils';

const ACCEPTED_AVATAR_TYPES = 'image/png,image/jpeg,image/webp,image/gif';
const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

const HiddenFileInput = styled('input')({
    display: 'none',
});

const AvatarUploadOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    color: theme.palette.common.white,
    borderRadius: '50%',
    opacity: 0,
    transition: 'opacity 0.15s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
        opacity: 1,
    },
}));

const AvatarWrapper = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: theme.spacing(12),
    height: theme.spacing(12),
    margin: theme.spacing(2),
}));

const AvatarInner = styled(Avatar)(({ theme }) => ({
    width: '100%',
    height: '100%',
}));

const AvatarRemoveOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(180, 0, 0, 0.55)',
    color: theme.palette.common.white,
    borderRadius: '50%',
    opacity: 0,
    transition: 'opacity 0.15s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
        opacity: 1,
    },
}));

const AvatarUploadingOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    color: theme.palette.common.white,
    borderRadius: '50%',
    opacity: 1,
}));

const ProfileContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
        display: 'flex',
        justifyContent: 'space-between',
    }
}));

const UserAvatarSkeleton = styled(Skeleton)(({ theme }) => ({
    width: theme.spacing(12),
    height: theme.spacing(12),
    margin: theme.spacing(2),
}))

const UserInfo = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
        marginBottom: theme.spacing(2),
        width: '100%',
    }
}));

const UserDetails = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(2),
        width: '100%',
    }
}));

const UserDetailsEdit = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-end',
    }
}));

const UserInfoEdit = styled(IconButton)(({ theme }) => ({
    marginLeft: theme.spacing(12),
    [theme.breakpoints.down('sm')]: {
        marginLeft: theme.spacing(0),
    }
}));

const TimeTracking = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    [theme.breakpoints.up('md')]: {
        padding: theme.spacing(2),
        width: '35%',
    }
}));

const UserNameContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
        width: '100%',
    }
}));    

const UserName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
    textAlign: 'left',
    marginRight: theme.spacing(2),
}));

const BoxTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    textAlign: 'left',
    marginRight: theme.spacing(2),
}));

const UserEmail = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.text.secondary,
    textAlign: 'left',
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
}));

const TimeBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
        padding: theme.spacing(2),
        alignItems: 'space-between',
    }
}));

const TimeBoxItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    [theme.breakpoints.up('md')]: {
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
    }
}));

const TimeBoxItemTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.text.secondary,
    textAlign: 'left',
    marginRight: theme.spacing(2),
}));

const TimeBoxItemValue = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
    textAlign: 'left',
    marginRight: theme.spacing(2),
}));

const DeactivateButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(1),
    textTransform: 'none',
}));

const DeactivateStatusChip = styled(Chip)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const AdminActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const SetPasswordButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(1),
    textTransform: 'none',
}));

const ProfileHeader = ({ user, isLoading, canSeeInternal }) => {
    const [timeProfile, setTimeProfile] = useState({ "todayTime": "-", "thisMonthTime": "-", "lastMonthTime": "-" });
    const [isEditing, setIsEditing] = useState(false);
    const [currentUser, setCurrentUser] = useState(user.fullName);
    const [editedUserFirstName, setEditedUserFirstName] = useState(user.first_name);
    const [editedUserLastName, setEditedUserLastName] = useState(user.last_name);
    const [currentAvatar, setCurrentAvatar] = useState(getAvatarUrl(user));
    const [editedAvatar, setEditedAvatar] = useState(getAvatarUrl(user));
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
    const [isRemovingAvatar, setIsRemovingAvatar] = useState(false);
    const [removeAvatarDialogOpen, setRemoveAvatarDialogOpen] = useState(false);
    const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
    const [setPasswordDialogOpen, setSetPasswordDialogOpen] = useState(false);
    const [isDeactivated, setIsDeactivated] = useState(user.active === false);
    const fileInputRef = useRef(null);
    const hasPermission = useHasPermission();
    const canEditUser = hasPermission('editUsers');
    const canSetUserPassword = hasPermission('adminUsers') || hasPermission('editUsersPassword');
    const canListUserTime = hasPermission('listUserTime');
    const dispatch = useDispatch();
    const isSelf = user._id === useSelector(state => state.auth.userId);
    const canAdminRemoveAvatar = !isSelf && canEditUser;

    useEffect(() => {
        if (!isLoading && canSeeInternal && (isSelf || canListUserTime)) {
            dispatch(getTimeProfile(user._id, (response) => {
                const formattedTimeProfile = convertMinutesToHours(response);
                setTimeProfile(formattedTimeProfile);
            }));
        }
    }, [user, canSeeInternal, canListUserTime, isSelf, dispatch, isLoading]);
    
    const handleUserEdit = () => {
        setIsEditing(true);
        setEditedUserFirstName(user.first_name);
        setEditedUserLastName(user.last_name);
        setEditedAvatar(getAvatarUrl(user));
    }

    const handleUserSave = () => {
        dispatch(
            updateUser({ userId: user._id, userData: { first_name: editedUserFirstName, last_name: editedUserLastName }, callback: (response) => {
                if (response?.user) {
                    setCurrentUser(response.user.fullName);
                    setCurrentAvatar(getAvatarUrl(response.user));
                    setIsEditing(false);
                }
            }})
        );
    }

    const handleUserCancel = () => {
        setIsEditing(false);
        setEditedUserFirstName(user.first_name);
        setEditedUserLastName(user.last_name);
        setEditedAvatar(getAvatarUrl(user));
    }

    const handleAvatarClick = () => {
        if (isUploadingAvatar) return;
        fileInputRef.current?.click();
    };

    const handleRemoveAvatarClick = (e) => {
        e?.stopPropagation();
        if (isRemovingAvatar) return;
        setRemoveAvatarDialogOpen(true);
    };

    const handleRemoveAvatarConfirm = () => {
        setRemoveAvatarDialogOpen(false);
        setIsRemovingAvatar(true);
        dispatch(removeUserAvatar({
            userId: user._id,
            callback: (response) => {
                setIsRemovingAvatar(false);
                const updated = response?.user;
                if (updated) {
                    const url = `${getAvatarUrl(updated)}?v=${Date.now()}`;
                    setCurrentAvatar(url);
                    setEditedAvatar(url);
                }
            },
        }));
    };

    const handleRemoveAvatarCancel = () => {
        setRemoveAvatarDialogOpen(false);
    };

    const handleAvatarFileChange = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        const allowed = ACCEPTED_AVATAR_TYPES.split(',');
        if (!allowed.includes(file.type)) {
            dispatch(showAlert({ severity: 'error', message: 'Unsupported image type. Use PNG, JPEG, WEBP, or GIF.' }));
            return;
        }
        if (file.size > MAX_AVATAR_BYTES) {
            dispatch(showAlert({ severity: 'error', message: 'Image is too large. Max size is 5 MB.' }));
            return;
        }

        const formData = new FormData();
        formData.append('avatar', file);

        setIsUploadingAvatar(true);
        dispatch(uploadUserAvatar({
            formData,
            callback: (response) => {
                setIsUploadingAvatar(false);
                const updated = response?.user;
                if (updated?.avatar) {
                    const url = `${getAvatarUrl(updated)}?v=${Date.now()}`;
                    setCurrentAvatar(url);
                    setEditedAvatar(url);
                }
            },
        }));
    };

    return (
        <Grid item xs={12} md={12} lg={12}>
            <ProfileContainer>
                <UserInfo>
                    {isLoading ? 
                        <>
                            <UserAvatarSkeleton variant="circular" /> 
                            <Box>
                                <Skeleton width={200} height={60} />
                                <Skeleton width={100} height={20} />
                                <Skeleton width={100} height={20} />
                            </Box>
                        </> : (
                        <>
                            {isSelf && (
                                <HiddenFileInput
                                    type="file"
                                    ref={fileInputRef}
                                    accept={ACCEPTED_AVATAR_TYPES}
                                    onChange={handleAvatarFileChange}
                                />
                            )}
                            {isEditing ? (
                                <>
                                    <AvatarWrapper>
                                        <AvatarInner src={editedAvatar} alt={editedUserFirstName + ' ' + editedUserLastName} />
                                        {isSelf && !isUploadingAvatar && (
                                            <AvatarUploadOverlay onClick={handleAvatarClick}>
                                                <CloudUpload />
                                            </AvatarUploadOverlay>
                                        )}
                                        {canAdminRemoveAvatar && user.avatar && !isRemovingAvatar && (
                                            <AvatarRemoveOverlay onClick={handleRemoveAvatarClick} title="Remove avatar">
                                                <PersonRemove />
                                            </AvatarRemoveOverlay>
                                        )}
                                        {(isUploadingAvatar || isRemovingAvatar) && (
                                            <AvatarUploadingOverlay>
                                                <CircularProgress size={28} color="inherit" />
                                            </AvatarUploadingOverlay>
                                        )}
                                    </AvatarWrapper>
                                    <UserDetailsEdit>
                                        <TextField label='First Name' type="text" value={editedUserFirstName} onChange={(e) => setEditedUserFirstName(e.target.value)} />
                                        <TextField label='Last Name' type="text" value={editedUserLastName} onChange={(e) => setEditedUserLastName(e.target.value)} />
                                        <Box>
                                            <IconButton onClick={handleUserSave}><Check /></IconButton>
                                            <IconButton onClick={handleUserCancel}><Close /></IconButton>
                                        </Box>
                                    </UserDetailsEdit>
                                </>
                            ) : (
                                <>
                                    <AvatarWrapper>
                                        <AvatarInner src={currentAvatar} alt={currentUser} />
                                        {isSelf && !isUploadingAvatar && (
                                            <AvatarUploadOverlay onClick={handleAvatarClick}>
                                                <CloudUpload />
                                            </AvatarUploadOverlay>
                                        )}
                                        {canAdminRemoveAvatar && user.avatar && !isRemovingAvatar && (
                                            <AvatarRemoveOverlay onClick={handleRemoveAvatarClick} title="Remove avatar">
                                                <PersonRemove />
                                            </AvatarRemoveOverlay>
                                        )}
                                        {(isUploadingAvatar || isRemovingAvatar) && (
                                            <AvatarUploadingOverlay>
                                                <CircularProgress size={28} color="inherit" />
                                            </AvatarUploadingOverlay>
                                        )}
                                    </AvatarWrapper>
                                    <UserDetails>
                                        <UserNameContainer>
                                            <UserName variant="h5">{currentUser} </UserName>
                                            {(isSelf || canEditUser) && <UserInfoEdit onClick={handleUserEdit}><Edit /></UserInfoEdit>}
                                        </UserNameContainer>
                                        <UserEmail variant="body2">{user.email}</UserEmail>
                                        <Chip label={user.title} size="small" variant="outlined" />
                                        {!isSelf && (canSetUserPassword || canEditUser) && (
                                            <AdminActions>
                                                {canSetUserPassword && (
                                                    <SetPasswordButton
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        startIcon={<LockReset />}
                                                        onClick={() => setSetPasswordDialogOpen(true)}
                                                    >
                                                        Set password
                                                    </SetPasswordButton>
                                                )}
                                                {canEditUser && (
                                                    isDeactivated ? (
                                                        <DeactivateStatusChip label="Deactivated" size="small" color="default" />
                                                    ) : (
                                                        <DeactivateButton
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                            startIcon={<PersonRemove />}
                                                            onClick={() => setDeactivateDialogOpen(true)}
                                                        >
                                                            Deactivate user
                                                        </DeactivateButton>
                                                    )
                                                )}
                                            </AdminActions>
                                        )}
                                    </UserDetails>
                                </>
                            )}
                            
                            
                        </>
                    )}
                </UserInfo>
                {canSeeInternal && (isSelf || canListUserTime) && (
                    <TimeTracking>
                        <BoxTitle variant="h5">Time Entered</BoxTitle>
                        <TimeBox>
                            <TimeBoxItem>
                                <TimeBoxItemTitle variant="subtitle2">Today</TimeBoxItemTitle>
                            <TimeBoxItemValue variant="body1">{timeProfile.todayTime === '-' ? '-' : timeProfile.todayTime}</TimeBoxItemValue>
                            </TimeBoxItem>
                            <TimeBoxItem>
                                <TimeBoxItemTitle variant="subtitle2">This Month</TimeBoxItemTitle>
                                <TimeBoxItemValue variant="body1">{timeProfile.thisMonthTime === '-' ? '-' : timeProfile.thisMonthTime}</TimeBoxItemValue>
                            </TimeBoxItem>
                            <TimeBoxItem>
                                <TimeBoxItemTitle variant="subtitle2">Last Month</TimeBoxItemTitle>
                                <TimeBoxItemValue variant="body1">{timeProfile.lastMonthTime === '-' ? '-' : timeProfile.lastMonthTime}</TimeBoxItemValue>
                            </TimeBoxItem>
                        </TimeBox>
                    </TimeTracking>
                )}
            </ProfileContainer>
            <Dialog open={removeAvatarDialogOpen} onClose={handleRemoveAvatarCancel}>
                <DialogTitle>Remove avatar?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will permanently remove this user's avatar image. This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRemoveAvatarCancel}>Cancel</Button>
                    <Button onClick={handleRemoveAvatarConfirm} color="error" variant="contained">Remove</Button>
                </DialogActions>
            </Dialog>
            <DeactivateUserDialog
                open={deactivateDialogOpen}
                user={user}
                onClose={() => setDeactivateDialogOpen(false)}
                onConfirm={() => dispatch(deactivateUser({
                    userId: user._id,
                    callback: (data) => {
                        if (data) {
                            setIsDeactivated(true);
                        }
                    },
                }))}
            />
            <SetUserPasswordDialog
                open={setPasswordDialogOpen}
                user={user}
                onClose={() => setSetPasswordDialogOpen(false)}
            />
        </Grid>
    );
};

export default ProfileHeader;